import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Clock, User, Wine as WineIcon, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { API } from "@/lib/apiBase";

export default function AdminPendingTab() {
  const [users, setUsers] = useState([]);
  const [wines, setWines] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [u, w, c] = await Promise.all([
        axios.get(`${API}/admin/pending/users`),
        axios.get(`${API}/admin/pending/wines`),
        axios.get(`${API}/admin/pending/credits`),
      ]);
      setUsers(u.data.users);
      setWines(w.data.wines);
      setCredits(c.data.credits);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load pending items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const act = (endpoint, successMsg) => async () => {
    try {
      await axios.post(`${API}${endpoint}`);
      toast.success(successMsg);
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Action failed");
    }
  };

  if (loading) return <div className="text-white/40 font-body text-sm">Loading...</div>;

  return (
    <div className="space-y-10" data-testid="admin-pending">
      <PendingSection
        title="Pending Users"
        icon={User}
        count={users.length}
        testId="pending-users-section"
        empty="No users awaiting approval."
      >
        {users.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {["Name", "Email", "Role", "Details", "Actions"].map((c) => (
                  <TableHead key={c} className={`text-white/40 font-body text-xs uppercase tracking-wider${c === "Actions" ? " text-right" : ""}`}>{c}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`pending-user-row-${u.id}`}>
                  <TableCell className="text-white/85 font-body text-sm">{u.name}</TableCell>
                  <TableCell className="text-white/60 font-body text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-gold/30 text-gold">{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-white/50 font-body text-xs">
                    {u.credential || u.business_name || "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <ApproveRejectButtons
                      onApprove={act(`/admin/pending/users/${u.id}/approve`, `Approved ${u.email}`)}
                      onReject={act(`/admin/pending/users/${u.id}/reject`, `Rejected ${u.email}`)}
                      approveTestId={`approve-user-${u.id}`}
                      rejectTestId={`reject-user-${u.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PendingSection>

      <PendingSection
        title="Pending Partner Wines"
        icon={WineIcon}
        count={wines.length}
        testId="pending-wines-section"
        empty="No partner-submitted wines awaiting review."
      >
        {wines.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {["Wine", "Year", "Region", "Submitted By", "Actions"].map((c) => (
                  <TableHead key={c} className={`text-white/40 font-body text-xs uppercase tracking-wider${c === "Actions" ? " text-right" : ""}`}>{c}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {wines.map((w) => (
                <TableRow key={w.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`pending-wine-row-${w.id}`}>
                  <TableCell className="text-white/85 font-body text-sm">{w.name}</TableCell>
                  <TableCell className="text-white/60 font-body text-sm">{w.year}</TableCell>
                  <TableCell className="text-white/60 font-body text-sm">{w.region}</TableCell>
                  <TableCell className="text-white/50 font-body text-xs">
                    {w.submitted_by_business || w.submitted_by_name}
                    <span className="text-white/30 block">{w.submitted_by_email}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <ApproveRejectButtons
                      onApprove={act(`/admin/pending/wines/${w.id}/approve`, `Approved "${w.name}"`)}
                      onReject={act(`/admin/pending/wines/${w.id}/reject`, `Rejected "${w.name}"`)}
                      approveTestId={`approve-wine-${w.id}`}
                      rejectTestId={`reject-wine-${w.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PendingSection>

      <PendingSection
        title="Pending Sommelier Credits"
        icon={Award}
        count={credits.length}
        testId="pending-credits-section"
        empty="No sommelier credits awaiting review."
      >
        {credits.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {["Wine", "Rating", "Notes", "Sommelier", "Actions"].map((c) => (
                  <TableHead key={c} className={`text-white/40 font-body text-xs uppercase tracking-wider${c === "Actions" ? " text-right" : ""}`}>{c}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {credits.map((c) => (
                <TableRow key={c.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`pending-credit-row-${c.id}`}>
                  <TableCell className="text-white/85 font-body text-sm">{c.wine_name}</TableCell>
                  <TableCell className="text-gold/80 font-body text-sm font-bold">{c.rating}</TableCell>
                  <TableCell className="text-white/50 font-body text-xs max-w-xs truncate">{c.notes || "—"}</TableCell>
                  <TableCell className="text-white/50 font-body text-xs">{c.user_email}</TableCell>
                  <TableCell className="text-right">
                    <ApproveRejectButtons
                      onApprove={act(`/admin/pending/credits/${c.id}/approve`, `Applied rating ${c.rating} to "${c.wine_name}"`)}
                      onReject={act(`/admin/pending/credits/${c.id}/reject`, "Credit rejected")}
                      approveTestId={`approve-credit-${c.id}`}
                      rejectTestId={`reject-credit-${c.id}`}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PendingSection>
    </div>
  );
}

function PendingSection({ title, icon: Icon, count, testId, empty, children }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <Icon className="w-4 h-4 text-merlot-light" />
        <p className="font-accent text-xs tracking-[0.3em] text-gold/60">{title.toUpperCase()}</p>
        <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">{count}</Badge>
      </div>
      {count === 0 ? (
        <div className="glass-card rounded-sm p-5 flex items-center gap-3 text-white/40 font-body text-sm">
          <Clock className="w-4 h-4" /> {empty}
        </div>
      ) : (
        <div className="glass-card rounded-sm overflow-hidden">{children}</div>
      )}
    </section>
  );
}

function ApproveRejectButtons({ onApprove, onReject, approveTestId, rejectTestId }) {
  return (
    <div className="inline-flex items-center gap-2">
      <Button
        size="sm"
        onClick={onApprove}
        data-testid={approveTestId}
        className="bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/30 h-8 gap-1"
      >
        <Check className="w-3.5 h-3.5" /> Approve
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onReject}
        data-testid={rejectTestId}
        className="border-merlot/40 text-merlot-light hover:bg-merlot/20 h-8 gap-1"
      >
        <X className="w-3.5 h-3.5" /> Reject
      </Button>
    </div>
  );
}
